import { useState, useEffect, useContext, Fragment } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Button } from '@material-ui/core';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, Tooltip } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import DateRangeModal from '../../common/date-range';
import WorkPlanCreate from '../components/work-plan-create';

import AuthContext from '../../context';
import { get } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isSameOrBefore);

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center'
    },
    createBtn: {
        marginRight: 15,
        ...themeColors.greenBtn
    },
    searchBtn: {
        ...themeColors.blueBtn
    },
    tableContainer: {
        borderRadius: 8,
        marginTop: 20
    },
    table: {
        marginBottom: 0
    },
    planned: {
        color: 'red'
    },
    commonCell: {
        minWidth: 50
    },
    weekCell: {
        minWidth: 50,
        backgroundColor: themeColors.gray[300]
    },
    nameCell: {
        maxWidth: 230,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        boxSizing: 'border-box'
    }
}));

const WorkPlan = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [state, setState] = useState({
        loading: true,
        products: [],
        weeks: [],
        planned: [],
        labels: {
            prebooksDate: dayjs().format('YYYY-MM-DD'),
            productionDateRange: `${dayjs().startOf('week').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
        },
        filters: {
            prebooksStartDate: dayjs().startOf('day').format(),
            prebooksEndDate: dayjs().endOf('day').format(),
            productionStartDate: dayjs().format('MM/DD/YYYY'),
            productionEndDate: dayjs().startOf('week').format('MM/DD/YYYY'),
            weeks: '',
            truckStartDate: '',
            truckEndDate: ''
        },
        rangeModals: {
            productionOpen: false,
            productionRange: [{
                key: 'main',
                startDate: new Date(dayjs().startOf('week').toDate()),
                endDate: new Date()
            }],
            weeksOpen: false,
            weeksRange: [{
                key: 'main',
                startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                endDate: new Date()
            }]
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Komet | Work plan';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions, loading: false }));
    }

    const search = async () => {
        showLoader(true);
        const response = await get('/kometWorkPlan/search', state.filters);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                products: response?.data ?? [],
                weeks: response?.weeks ?? [],
                planned: response?.planned ?? []
            }));
        } else {
            console.log('[WorkPlan] search error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handlePrebooksDateChange = ({ target }) => setState(prev => ({
        ...prev,
        labels: {
            ...prev.labels,
            [target.name]: target.value
        },
        filters: {
            ...prev.filters,
            prebooksStartDate: dayjs(target.value).startOf('day').format(),
            prebooksEndDate: dayjs(target.value).endOf('day').format()
        }
    }));

    const handleDateRangeChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;

        setState(prev => ({
            ...prev,
            labels: {
                ...prev.labels,
                productionDateRange: dateRange
            },
            filters: {
                ...prev.filters,
                productionStartDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                productionEndDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModals: { ...prev.rangeModals, productionRange: [item.main] }
        }));
    }

    const handleWeeksChange = item => {
        const { startDate, endDate } = item.main;
        const startISO = dayjs(startDate).startOf('isoWeek');
        const endISO = dayjs(endDate).endOf('isoWeek');

        let start = startISO;

        const weeks = [];

        while (start.isSameOrBefore(endISO)) {
            weeks.push(`${start.isoWeekYear()}-${start.isoWeek()}`);

            start = start.add(1, 'week');
        }

        setState(prev => ({
            ...prev,
            filters: { ...prev.filters, weeks: weeks.join(), truckStartDate: startISO.format('YYYY-MM-DD'), truckEndDate: endISO.format('YYYY-MM-DD') },
            rangeModals: { ...prev.rangeModals, weeksRange: [{ ...item.main, startDate: startISO.toDate(), endDate: endISO.toDate() }] }
        }));
    }

    const toggleRangeModal = prefix => setState(prev => ({ ...prev, rangeModals: { ...prev.rangeModals, [`${prefix}Open`]: !prev.rangeModals[[`${prefix}Open`]] } }));

    const toggleShowForm = () => setShowCreateForm(prev => !prev);

    const getPlannedTotal = code => {
        const productWorkPlan = state.planned.filter(wp => wp['recipe.code'] === code);
        const total = productWorkPlan.reduce((acc, wp) => acc + Number(wp.qty), 0);

        return total;
    }

    const getWeekTotal = dates => {
        const total = dates.reduce((sum, d) => sum + d.records.reduce((s, r) => {
            if (r.presold_quantity === r.billed_boxes && r.mark_as_completed === 'Yes') return s + Number(r.presold_quantity);

            return s + Number(r.billed_boxes);
        }, 0), 0);

        return total;
    }

    const getDateTotal = records => {
        const total = records.reduce((sum, r) => {
            if (r.presold_quantity === r.billed_boxes && r.mark_as_completed === 'Yes') return sum + Number(r.presold_quantity);

            return sum + Number(r.billed_boxes);
        }, 0);

        return total;
    }

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr) return <NotFound />;

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Prebook summary' />

            <Grid container>
                <Grid item xs={12} md={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Prebooks date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='date'
                                placeholder='Date'
                                name='prebooksDate'
                                value={state.labels.prebooksDate}
                                onChange={handlePrebooksDateChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={3} xl={2}>
                    <FormGroup onClick={() => toggleRangeModal('production')}>
                        <FormLabel>{'Production date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                type='text'
                                placeholder='Date'
                                value={state.labels.productionDateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={12} md={3} xl={2}>
                    <FormGroup onClick={() => toggleRangeModal('weeks')}>
                        <FormLabel>{'Weeks'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                type='text'
                                placeholder='Week range'
                                value={state.filters.weeks}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>

                <Grid item xs={12} md={6} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        className={classes.createBtn}
                        onClick={toggleShowForm}
                    >
                        {'Create work plan'}
                    </Button>
                    <Button
                        variant='contained'
                        className={classes.searchBtn}
                        onClick={search}
                    >
                        {'Search'}
                    </Button>
                </Grid>
            </Grid>

            <WorkPlanCreate open={showCreateForm} permissions={state.permissions} onStore={search} />

            <Divider />

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table size='small' className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{'Code'}</TableCell>
                            <TableCell>{'Product'}</TableCell>
                            <TableCell align='right'>{'Grand total'}</TableCell>
                            <TableCell align='right'>{'Planned'}</TableCell>
                            <TableCell align='right'>{'Production'}</TableCell>
                            {
                                state.weeks.map(rw => {
                                    const { week, dates } = rw;

                                    return (
                                        <Fragment key={week}>
                                            {
                                                dates.map(date => <TableCell key={date} align='right' >{dayjs(date).format('MM/DD')}</TableCell>)
                                            }
                                            <TableCell align='right' className={classes.weekCell}>{week}</TableCell>
                                        </Fragment>
                                    );
                                })
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            state.products.map(product =>
                                <TableRow hover key={product.legacy_code}>
                                    <TableCell>{product.legacy_code}</TableCell>
                                    <Tooltip title={product.product}>
                                        <TableCell className={classes.nameCell}>{product.product}</TableCell>
                                    </Tooltip>
                                    <TableCell align='right'>{product?.gt ?? 0}</TableCell>
                                    <TableCell align='right' className={classes.planned}>{getPlannedTotal(product.legacy_code)}</TableCell>
                                    <TableCell align='right'>{product?.production ?? 0}</TableCell>
                                    {
                                        state.weeks.map(rw => {
                                            const productWeek = product.weeks.find(item => item.week === rw.week);

                                            return (
                                                <Fragment key={rw.week}>
                                                    {
                                                        rw.dates.map(date => {
                                                            const dayPlanned = state.planned.find(wp => wp['recipe.code'] === product.legacy_code && wp['date'] === date);

                                                            // muestra el work plan del (producto + fecha) cuando NO hayan prebooks en toda la semana de dicho producto
                                                            if (productWeek === undefined) return (
                                                                <TableCell key={date} align='right' className={classes.commonCell}>
                                                                    {(dayPlanned !== undefined) && <span className={classes.planned}>{`(${dayPlanned.qty})`}</span>}
                                                                </TableCell>
                                                            );
                                                            const productDate = productWeek.dates.find(item => item.date === date);

                                                            // muestra el work plan del (producto + fecha) cuando SI hayan prebooks en toda la semana de dicho producto, pero NO cuando hayan prebooks en la fecha en concreto
                                                            if (productWeek !== undefined && productDate === undefined) return (
                                                                <TableCell key={date} align='right' className={classes.commonCell}>
                                                                    {(dayPlanned !== undefined) && <span className={classes.planned}>{`(${dayPlanned.qty})`}</span>}
                                                                </TableCell>
                                                            );

                                                            // muestra los prebooks cuando hayan datos en la semana y el work plan si es que hay
                                                            return (
                                                                <TableCell key={date} align='right' className={classes.commonCell}>
                                                                    {getDateTotal(productDate.records)}
                                                                    {(dayPlanned !== undefined) && <span className={classes.planned}>{` (${dayPlanned.qty})`}</span>}
                                                                </TableCell>
                                                            );
                                                        })
                                                    }
                                                    <TableCell align='right' className={classes.weekCell}>
                                                        {productWeek && getWeekTotal(productWeek.dates)}
                                                    </TableCell>
                                                </Fragment>
                                            );
                                        })
                                    }
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <DateRangeModal
                open={state.rangeModals.productionOpen}
                range={state.rangeModals.productionRange}
                onClose={() => toggleRangeModal('production')}
                onChange={handleDateRangeChange}
            />

            <DateRangeModal
                open={state.rangeModals.weeksOpen}
                range={state.rangeModals.weeksRange}
                onClose={() => toggleRangeModal('weeks')}
                onChange={handleWeeksChange}
            />
        </div >
    );
}

export default WorkPlan;